import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api, { money, formatApiError } from "@/lib/api";
import RoleShell from "@/components/RoleShell";
import { Stat, StatusBadge, VehicleBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LayoutDashboard, Users, Store, ClipboardList, Package, Tag, Truck, Wallet, SlidersHorizontal, ScrollText, ShoppingBag, Percent, Activity, Globe, Bell } from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "stores", label: "Stores", icon: Store },
  { key: "users", label: "Users", icon: Users },
  { key: "orders", label: "Orders", icon: ClipboardList },
  { key: "products", label: "Products", icon: Package },
  { key: "deliveries", label: "Deliveries", icon: Truck },
  { key: "finance", label: "Finance", icon: Wallet },
  { key: "categories", label: "Categories", icon: Tag },
  { key: "countries", label: "Countries", icon: Globe },
  { key: "pricing", label: "Pricing", icon: SlidersHorizontal },
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "audit", label: "Audit", icon: ScrollText },
];

export default function AdminApp() {
  const { t } = useTranslation();
  const [active, setActive] = useState("overview");
  return (
    <RoleShell title={t("roles.admin")} tabs={TABS} active={active} setActive={setActive}>
      {active === "overview" && <Overview />}
      {active === "stores" && <Stores />}
      {active === "users" && <UsersTab />}
      {active === "orders" && <OrdersTab />}
      {active === "products" && <ProductsTab />}
      {active === "deliveries" && <Deliveries />}
      {active === "finance" && <Finance />}
      {active === "categories" && <Categories />}
      {active === "countries" && <Countries />}
      {active === "pricing" && <Pricing />}
      {active === "notifications" && <Notifications />}
      {active === "audit" && <Audit />}
    </RoleShell>
  );
}

function Overview() {
  const { t } = useTranslation();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label={t("roles.orders")} value={s.orders} icon={ShoppingBag} />
        <Stat label={t("admin.activeOrders")} value={s.active_orders} icon={Activity} accent="text-sky-500" />
        <Stat label={t("admin.gmv")} value={money(s.gmv)} icon={Wallet} accent="text-emerald-500" />
        <Stat label={t("admin.platformRevenue")} value={money(s.revenue)} icon={Percent} accent="text-brand" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label={t("admin.customers")} value={s.customers} icon={Users} />
        <Stat label={t("admin.merchants")} value={s.merchants} icon={Store} />
        <Stat label={t("admin.couriers")} value={s.couriers} icon={Truck} />
        <Stat label={t("admin.pendingStores")} value={s.pending_stores} icon={Store} accent="text-amber-500" />
      </div>
    </div>
  );
}

function Stores() {
  const { t } = useTranslation();
  const [stores, setStores] = useState([]);
  const load = useCallback(() => api.get("/admin/stores").then((r) => setStores(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => { await api.post(`/admin/stores/${id}/status`, { status }); toast.success(t("admin.statusUpdated")); load(); };
  return (
    <div className="space-y-3">
      {stores.map((s) => (
        <div key={s.id} data-testid={`admin-store-${s.id}`} className="rounded-2xl border bg-card p-4 flex items-center justify-between flex-wrap gap-3">
          <div><div className="font-semibold">{s.name}</div><div className="text-xs text-muted-foreground">{s.category} · {s.city} · ⭐ {s.rating}</div></div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-1 rounded-full border ${s.status === "approved" ? "bg-emerald-50 text-emerald-700" : s.status === "pending" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`}>{t(`admin.storeStatus.${s.status}`, s.status)}</span>
            {s.status !== "approved" && <Button data-testid={`approve-${s.id}`} size="sm" className="rounded-full bg-brand hover:bg-brand-dark" onClick={() => setStatus(s.id, "approved")}>{t("admin.approve")}</Button>}
            {s.status !== "suspended" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setStatus(s.id, "suspended")}>{t("admin.suspend")}</Button>}
          </div>
        </div>
      ))}
    </div>
  );
}

function UsersTab() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [role, setRole] = useState("");
  const load = useCallback(() => api.get("/admin/users", { params: role ? { role } : {} }).then((r) => setUsers(r.data)), [role]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (u) => { await api.post(`/admin/users/${u.id}/status`, { status: u.status === "suspended" ? "active" : "suspended" }); load(); };
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {["", "customer", "merchant", "courier"].map((r) => (
          <button key={r} data-testid={`filter-${r || "all"}`} onClick={() => setRole(r)} className={`px-3 py-1.5 rounded-full text-sm border ${role === r ? "bg-brand text-white border-brand" : "bg-card"}`}>{r ? t(`roles.${r}`, r) : t("common.all")}</button>
        ))}
      </div>
      <div className="rounded-2xl border bg-card overflow-hidden divide-y">
        {users.map((u) => (
          <div key={u.id} className="flex items-center justify-between p-3 text-sm">
            <div><span className="font-medium">{u.name}</span> <span className="text-muted-foreground">· {u.email}</span><span className="ms-2 text-xs px-2 py-0.5 rounded-full bg-muted">{t(`roles.${u.role}`, u.role)}</span></div>
            <Button size="sm" variant={u.status === "suspended" ? "outline" : "ghost"} onClick={() => toggle(u)} className="rounded-full">{u.status === "suspended" ? t("admin.reactivate") : t("admin.suspend")}</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrdersTab() {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/admin/orders").then((r) => setOrders(r.data)); }, []);
  return (
    <div className="space-y-3">
      {orders.map((o) => (
        <div key={o.id} className="rounded-2xl border bg-card p-4 flex items-center justify-between flex-wrap gap-2">
          <div><div className="font-semibold text-sm">{o.store_name} → {o.customer_name}</div><div className="text-xs text-muted-foreground font-mono">#{o.id.slice(-8)} · {money(o.total)} · {t(`admin.payStatus.${o.payment_status}`, o.payment_status)}</div></div>
          <StatusBadge status={o.status} />
        </div>
      ))}
      {orders.length === 0 && <div className="text-center py-16 text-muted-foreground">{t("admin.noOrders")}</div>}
    </div>
  );
}

function ProductsTab() {
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/admin/products").then((r) => setProducts(r.data)); }, []);
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {products.map((p) => (
        <div key={p.id} className="rounded-2xl border bg-card p-4 flex gap-3">
          <div className="w-14 h-14 rounded-xl bg-muted overflow-hidden shrink-0">{p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}</div>
          <div className="min-w-0"><div className="font-semibold text-sm truncate">{p.name}</div><div className="text-xs text-muted-foreground">{p.store_name}</div><div className="text-brand font-bold text-sm mt-1">{money(p.price)}</div></div>
        </div>
      ))}
    </div>
  );
}

function Deliveries() {
  const { t } = useTranslation();
  const [d, setD] = useState([]);
  useEffect(() => { api.get("/admin/deliveries").then((r) => setD(r.data)); }, []);
  return (
    <div className="space-y-3">
      {d.map((o) => (
        <div key={o.id} className="rounded-2xl border bg-card p-4 flex items-center justify-between flex-wrap gap-2">
          <div><div className="font-semibold text-sm">{o.courier_name}</div><div className="text-xs text-muted-foreground">{o.store_name} → {o.address?.city} · {o.distance_km}km</div></div>
          <div className="flex items-center gap-2"><VehicleBadge type={o.recommended_vehicle} active /><StatusBadge status={o.status} /></div>
        </div>
      ))}
      {d.length === 0 && <div className="text-center py-16 text-muted-foreground">{t("admin.noDeliveries")}</div>}
    </div>
  );
}

function Finance() {
  const { t } = useTranslation();
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/admin/finance").then((r) => setF(r.data)); }, []);
  if (!f) return null;
  const rows = [
    [t("admin.gross"), f.gmv, false], [t("merchant.commission"), f.commission, false], [t("admin.serviceFees"), f.service_fees, false],
    [t("admin.deliveryFees"), f.delivery_fees, false], [t("admin.merchantPayouts"), f.merchant_payouts, false],
    [t("admin.courierEarnings"), f.courier_earnings, false], [t("admin.platformRevenue"), f.platform_revenue, true],
  ];
  return (
    <div className="max-w-lg rounded-2xl border bg-card p-5 space-y-1">
      {rows.map(([l, v, isTotal], i) => (
        <div key={i} className={`flex justify-between py-2.5 ${isTotal ? "border-t mt-1 font-bold text-brand" : "border-b last:border-0 text-sm"}`}>
          <span className={isTotal ? "" : "text-muted-foreground"}>{l}</span><span className="font-semibold">{money(v)}</span>
        </div>
      ))}
    </div>
  );
}

function Categories() {
  const { t } = useTranslation();
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const load = useCallback(() => api.get("/categories").then((r) => setCats(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => { if (!name) return; await api.post("/admin/categories", { name, order: cats.length + 1, subcategories: [] }); setName(""); toast.success(t("admin.categoryAdded")); load(); };
  const del = async (id) => { await api.delete(`/admin/categories/${id}`); load(); };
  return (
    <div className="max-w-2xl space-y-4">
      <div className="flex gap-2">
        <Input data-testid="new-cat-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("admin.newCategory")} className="rounded-xl" />
        <Button data-testid="add-cat" onClick={add} className="rounded-full bg-brand hover:bg-brand-dark shrink-0">{t("common.add")}</Button>
      </div>
      <div className="rounded-2xl border bg-card divide-y">
        {cats.map((c) => (
          <div key={c.id} className="flex items-center justify-between p-3">
            <div><div className="font-medium text-sm">{c.name}</div><div className="text-xs text-muted-foreground">{c.subcategories?.join(", ")}</div></div>
            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del(c.id)}>{t("common.delete")}</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Pricing() {
  const { t } = useTranslation();
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/admin/pricing").then((r) => setP(r.data)); }, []);
  const save = async () => { await api.put("/admin/pricing", p); toast.success(t("admin.pricingUpdated")); };
  if (!p) return null;
  const setNum = (k) => (e) => setP({ ...p, [k]: parseFloat(e.target.value) || 0 });
  const setVeh = (v, k) => (e) => setP({ ...p, vehicles: { ...p.vehicles, [v]: { ...p.vehicles[v], [k]: parseFloat(e.target.value) || 0 } } });
  return (
    <div className="max-w-3xl space-y-5">
      <div className="rounded-2xl border bg-card p-5 grid sm:grid-cols-3 gap-4">
        <Field label={t("admin.commissionPct")} value={p.commission_percent} testid="pr-commission" disabled hint={t("admin.commissionFixed")} />
        <Field label={t("admin.serviceFeePct")} value={p.service_fee_percent} onChange={setNum("service_fee_percent")} />
        <Field label={t("admin.courierSharePct")} value={p.courier_share_percent} onChange={setNum("courier_share_percent")} />
        <Field label={t("admin.weightFee")} value={p.weight_fee_per_kg} onChange={setNum("weight_fee_per_kg")} />
        <Field label={t("admin.heavySurcharge")} value={p.heavy_item_surcharge} onChange={setNum("heavy_item_surcharge")} />
        <Field label={t("admin.peakMultiplier")} value={p.peak_multiplier} onChange={setNum("peak_multiplier")} />
      </div>
      <div className="rounded-2xl border bg-card p-5 space-y-3">
        <div className="font-semibold">{t("admin.vehicleTiers")}</div>
        {Object.keys(p.vehicles).map((v) => (
          <div key={v} className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
            <div className="font-medium text-sm sm:col-span-1">{t(`vehicle.${v}`, v)}</div>
            <Field label={t("admin.maxKg")} value={p.vehicles[v].max_weight_kg} onChange={setVeh(v, "max_weight_kg")} small />
            <Field label={t("admin.maxM3")} value={p.vehicles[v].max_volume_m3} onChange={setVeh(v, "max_volume_m3")} small />
            <Field label={t("admin.maxPkg")} value={p.vehicles[v].max_packages} onChange={setVeh(v, "max_packages")} small />
            <Field label={t("admin.baseFee")} value={p.vehicles[v].base_fee} onChange={setVeh(v, "base_fee")} small />
            <Field label={t("admin.perKm")} value={p.vehicles[v].per_km} onChange={setVeh(v, "per_km")} small />
          </div>
        ))}
      </div>
      <Button data-testid="save-pricing" onClick={save} className="rounded-full bg-brand hover:bg-brand-dark">{t("admin.savePricing")}</Button>
    </div>
  );
}
function Field({ label, value, onChange, testid, small, disabled, hint }) {
  return (
    <div>
      <label className={`block ${small ? "text-[10px]" : "text-xs"} text-muted-foreground mb-1`}>{label}</label>
      <Input data-testid={testid} type="number" value={value} onChange={onChange} disabled={disabled} readOnly={disabled} className={`rounded-lg h-9 ${disabled ? "bg-muted cursor-not-allowed opacity-70" : ""}`} />
      {hint && <p className="text-[10px] text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

function Countries() {
  const { t } = useTranslation();
  const empty = { code: "", name: "", currency: "", locale: "", languages: "", default_language: "", timezone: "", tax: { name: "VAT", rate: 0, inclusive: true }, phone_format: "", address_format: "", payment_providers: "mock:Mock (test)", active: true };
  const [list, setList] = useState([]);
  const [f, setF] = useState(empty);
  const [editing, setEditing] = useState(false);
  const load = useCallback(() => api.get("/admin/countries").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const editRow = (c) => {
    setEditing(true);
    setF({ ...c, languages: (c.languages || []).join(","), payment_providers: (c.payment_providers || []).map((p) => `${p.key}:${p.label}${p.default ? "*" : ""}`).join(","), tax: c.tax || { name: "VAT", rate: 0, inclusive: true } });
  };
  const save = async () => {
    const payload = {
      code: f.code, name: f.name, currency: f.currency, locale: f.locale,
      languages: f.languages.split(",").map((x) => x.trim()).filter(Boolean),
      default_language: f.default_language || undefined, timezone: f.timezone,
      tax: { name: f.tax.name, rate: parseFloat(f.tax.rate) || 0, inclusive: !!f.tax.inclusive },
      phone_format: f.phone_format, address_format: f.address_format, active: !!f.active,
      payment_providers: f.payment_providers.split(",").map((x) => x.trim()).filter(Boolean).map((pair) => {
        const def = pair.endsWith("*"); const [key, ...rest] = pair.replace(/\*$/, "").split(":");
        return { key: key.trim(), label: (rest.join(":") || key).trim(), default: def };
      }),
    };
    try {
      if (editing) await api.put(`/admin/countries/${f.code}`, payload);
      else await api.post("/admin/countries", payload);
      toast.success(t("admin.countrySaved")); setF(empty); setEditing(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("common.saveFailed")); }
  };
  const toggle = async (c) => { await api.put(`/admin/countries/${c.code}`, { active: !c.active }); toast.success(t("admin.statusUpdated")); load(); };

  const fld = (k, label, ph) => (
    <div><label className="block text-xs text-muted-foreground mb-1">{label}</label>
      <Input data-testid={`country-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="rounded-lg h-9" placeholder={ph} /></div>
  );

  return (
    <div className="max-w-4xl space-y-5">
      <div className="rounded-2xl border bg-card p-5 grid sm:grid-cols-3 gap-3">
        {fld("code", t("admin.code"), "IL")}
        {fld("name", t("admin.countryName"), "Israel")}
        {fld("currency", t("form.currency"), "ILS")}
        {fld("locale", t("form.locale"), "he-IL")}
        {fld("languages", t("form.languages"), t("admin.languagesHint"))}
        {fld("default_language", t("admin.defaultLanguage"), "he")}
        {fld("timezone", t("form.timezone"), "Asia/Jerusalem")}
        <div><label className="block text-xs text-muted-foreground mb-1">{t("admin.taxName")}</label>
          <Input data-testid="country-tax-name" value={f.tax.name} onChange={(e) => setF({ ...f, tax: { ...f.tax, name: e.target.value } })} className="rounded-lg h-9" /></div>
        <div><label className="block text-xs text-muted-foreground mb-1">{t("admin.taxRate")}</label>
          <Input data-testid="country-tax-rate" type="number" value={f.tax.rate} onChange={(e) => setF({ ...f, tax: { ...f.tax, rate: e.target.value } })} className="rounded-lg h-9" /></div>
        <label className="flex items-center gap-2 text-sm mt-5"><Switch data-testid="country-tax-incl" checked={!!f.tax.inclusive} onCheckedChange={(v) => setF({ ...f, tax: { ...f.tax, inclusive: v } })} /> {t("admin.taxInclusive")}</label>
        {fld("phone_format", t("admin.phoneFormat"), "+972-5X-XXX-XXXX")}
        {fld("address_format", t("admin.addressFormat"), "{line1}, {city} {postal}")}
        {fld("payment_providers", t("admin.paymentProviders"), t("admin.providersHint"))}
        <label className="flex items-center gap-2 text-sm mt-5"><Switch data-testid="country-active" checked={!!f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /> {t("form.active")}</label>
      </div>
      <div className="flex gap-2">
        <Button data-testid="save-country" onClick={save} className="rounded-full bg-brand hover:bg-brand-dark">{editing ? t("admin.editCountry") : t("admin.addCountry")}</Button>
        {editing && <Button variant="outline" className="rounded-full" onClick={() => { setF(empty); setEditing(false); }}>{t("common.reset")}</Button>}
      </div>

      <div className="rounded-2xl border bg-card divide-y">
        {list.length === 0 ? <div className="p-6 text-center text-muted-foreground text-sm">{t("admin.noCountries")}</div> :
          list.map((c) => (
            <div key={c.code} data-testid={`country-row-${c.code}`} className="flex items-center justify-between p-3">
              <div><div className="font-semibold text-sm">{c.name} · {c.currency} · {c.locale}</div>
                <div className="text-xs text-muted-foreground">{(c.languages || []).join(", ")} · {c.tax?.name} {c.tax?.rate}% {c.tax?.inclusive ? t("admin.incl") : t("admin.excl")}</div></div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded-full border ${c.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{c.active ? t("admin.active") : t("admin.inactive")}</span>
                <Button size="sm" variant="outline" className="rounded-full" data-testid={`edit-country-${c.code}`} onClick={() => editRow(c)}>{t("common.edit")}</Button>
                <Button size="sm" variant="ghost" className="rounded-full" data-testid={`toggle-country-${c.code}`} onClick={() => toggle(c)}>{c.active ? t("admin.suspend") : t("admin.approve")}</Button>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

function NotifRow({ k, v }) {
  return <div className="flex justify-between gap-3"><span className="text-muted-foreground">{k}</span><span className="font-medium text-end break-all">{v}</span></div>;
}

function Notifications() {
  const { t, i18n } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [channel, setChannel] = useState("");
  const [event, setEvent] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      if (channel) params.channel = channel;
      if (event) params.event = event;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await api.get("/admin/notifications", { params });
      setLogs(r.data);
    } catch { setError(true); } finally { setLoading(false); }
  }, [q, status, channel, event, dateFrom, dateTo]);
  useEffect(() => { const id = setTimeout(load, 300); return () => clearTimeout(id); }, [load]);

  const retry = async (log) => {
    try {
      const r = await api.post(`/admin/notifications/${log.id}/retry`);
      toast.success(r.data.already_sent ? t("admin.notif.alreadySent") : t("admin.notif.retried"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("common.actionFailed")); }
  };

  const stateBadge = (st) => {
    const map = { sent: "bg-emerald-50 text-emerald-700 border-emerald-200", failed: "bg-red-50 text-red-700 border-red-200", pending: "bg-amber-50 text-amber-700 border-amber-200" };
    return <span data-testid={`notif-state-${st}`} className={`text-xs px-2 py-0.5 rounded-full border ${map[st] || "bg-slate-100 text-slate-600"}`}>{t(`admin.notif.${st}`, st)}</span>;
  };
  const sel = "h-9 px-3 rounded-lg border bg-card text-sm";
  const EVENTS = ["confirmed", "preparing", "courier_assigned", "arriving", "arriving_soon", "picked_up", "on_the_way", "courier_arrived", "delivered", "cancelled", "payment_failed", "refund"];

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-2">
        <Input data-testid="notif-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin.notif.search")} className="rounded-lg h-9 lg:col-span-2" />
        <select data-testid="notif-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className={sel}>
          <option value="">{t("admin.notif.allStatuses")}</option>
          <option value="sent">{t("admin.notif.sent")}</option>
          <option value="failed">{t("admin.notif.failed")}</option>
          <option value="pending">{t("admin.notif.pending")}</option>
        </select>
        <select data-testid="notif-filter-channel" value={channel} onChange={(e) => setChannel(e.target.value)} className={sel}>
          <option value="">{t("admin.notif.allChannels")}</option>
          <option value="email">Email</option>
        </select>
        <select data-testid="notif-filter-event" value={event} onChange={(e) => setEvent(e.target.value)} className={sel}>
          <option value="">{t("admin.notif.allEvents")}</option>
          {EVENTS.map((e) => <option key={e} value={e}>{t(`admin.notifEvent.${e}`, e)}</option>)}
        </select>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-2 items-center">
        <div className="lg:col-span-2"><label className="block text-[10px] text-muted-foreground mb-0.5">{t("admin.notif.dateFrom")}</label><Input data-testid="notif-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-lg h-9" /></div>
        <div className="lg:col-span-2"><label className="block text-[10px] text-muted-foreground mb-0.5">{t("admin.notif.dateTo")}</label><Input data-testid="notif-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-lg h-9" /></div>
        <Button size="sm" variant="ghost" data-testid="notif-clear-filters" onClick={() => { setQ(""); setStatus(""); setChannel(""); setEvent(""); setDateFrom(""); setDateTo(""); }} className="rounded-full lg:col-span-2 lg:justify-self-end">{t("admin.notif.clearFilters")}</Button>
      </div>
      <div className="text-xs text-muted-foreground" data-testid="notif-count">{logs.length} {t("admin.notif.count")}</div>
      {loading ? <div className="text-center py-16 text-muted-foreground text-sm">{t("common.loading")}</div>
        : error ? <div className="text-center py-16 text-red-600 text-sm" data-testid="notif-error">{t("admin.notif.loadError")}</div>
        : logs.length === 0 ? <div className="text-center py-16 text-muted-foreground text-sm" data-testid="notif-empty">{t("admin.notif.noLogs")}</div>
        : (
        <div className="rounded-2xl border bg-card divide-y" data-testid="notif-list">
          {logs.map((l) => (
            <div key={l.id} data-testid={`notif-row-${l.id}`} className="flex items-center justify-between p-3 gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="font-medium text-sm flex items-center gap-2 flex-wrap">{t(`admin.notifEvent.${l.event}`, l.event)} {stateBadge(l.delivery_state)}</div>
                <div className="text-xs text-muted-foreground truncate">{l.customer_name || "—"} · {l.to} · #{(l.order_id || "").slice(-8)} · {new Date(l.created_at).toLocaleString(i18n.language)}</div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="rounded-full" data-testid={`notif-details-${l.id}`} onClick={() => setDetail(l)}>{t("admin.notif.details")}</Button>
                {l.delivery_state === "failed" && <Button size="sm" className="rounded-full bg-brand hover:bg-brand-dark" data-testid={`notif-retry-${l.id}`} onClick={() => retry(l)}>{t("admin.notif.retry")}</Button>}
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-md" data-testid="notif-detail-dialog">
          <DialogHeader><DialogTitle>{detail && t(`admin.notifEvent.${detail.event}`, detail.event)}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              <NotifRow k={t("admin.notif.status")} v={t(`admin.notif.${detail.delivery_state}`, detail.delivery_state)} />
              <NotifRow k={t("admin.notif.recipient")} v={detail.to} />
              <NotifRow k={t("admin.notif.order")} v={`#${(detail.order_id || "").slice(-8)}`} />
              <NotifRow k={t("admin.notif.channel")} v={detail.channel} />
              <NotifRow k={t("admin.notif.language")} v={detail.language} />
              <NotifRow k={t("admin.notif.currency")} v={detail.currency} />
              <NotifRow k={t("admin.notif.provider")} v={detail.provider_id || "—"} />
              <NotifRow k={t("admin.notif.createdAt")} v={new Date(detail.created_at).toLocaleString(i18n.language)} />
              {detail.sent_at && <NotifRow k={t("admin.notif.sentAt")} v={new Date(detail.sent_at).toLocaleString(i18n.language)} />}
              {detail.error && <div><div className="text-xs text-muted-foreground mb-1">{t("admin.notif.failureReason")}</div><div className="rounded-lg bg-red-50 text-red-700 text-xs p-2 break-words" data-testid="notif-detail-error">{detail.error}</div></div>}
              {detail.delivery_state === "failed" && <Button className="w-full rounded-full bg-brand hover:bg-brand-dark" data-testid="notif-detail-retry" onClick={() => { retry(detail); setDetail(null); }}>{t("admin.notif.retry")}</Button>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Audit() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setLogs(r.data)); }, []);
  return (
    <div className="rounded-2xl border bg-card divide-y">
      {logs.length === 0 ? <div className="p-6 text-center text-muted-foreground text-sm">{t("admin.noAudit")}</div> :
        logs.map((l) => (
          <div key={l.id} className="p-3 text-sm flex justify-between">
            <div><span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{l.action}</span> <span className="text-muted-foreground">{l.actor_email}</span></div>
            <span className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString()}</span>
          </div>
        ))}
    </div>
  );
}
